/** @jsx jsx */
import { Paragraph, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "@eworkit/iris";
import { FC, useContext, useMemo } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { formatLoggedTime, TimeReport } from "@/data/time-reports";
import { TimeReportProjectSummaryItem } from "@/data/time-reports/types/TimeReportProjectSummaryItem";
import { groupTimeReportProjectSummaryItems } from "@/data/time-reports/utils/groupTimeReportProjectSummaryItems";
import { useFormats } from "@/hooks/Formats";

interface TimeReportSummaryTableProps {
  variant: "hourly" | "daily";
  timeReport: TimeReport;
  items: TimeReportProjectSummaryItem[];
}

export const TimeReportSummaryTable: FC<TimeReportSummaryTableProps> = ({ variant, timeReport, items }) => {
  const { t } = useTranslation("session");
  const { hasCompanyManagerPrivileges } = useContext(UserContext);
  const { formatCost, formatRate } = useFormats();

  const showAllColumns = hasCompanyManagerPrivileges();

  const [totalTime, totalCost] = items.reduce<[number, number]>(
    (acc, v) => [acc[0] + v.reportedTime, acc[1] + v.cost],
    [0, 0],
  );

  const groupedItems = useMemo(() => groupTimeReportProjectSummaryItems(items), [items]);

  return (
    <Table>
      <TableHeader>
        <TableHeaderCell width={showAllColumns ? "25%" : "50%"}>
          {t("TIME_REPORTS.SUMMARY.PROJECTS.PAYCODE")}
        </TableHeaderCell>
        <TableHeaderCell width={showAllColumns ? "25%" : "50%"}>
          {variant === "hourly"
            ? t("TIME_REPORTS.SUMMARY.PROJECTS.REPORTED_TIME_HOURLY")
            : t("TIME_REPORTS.SUMMARY.PROJECTS.REPORTED_TIME_DAILY")}
        </TableHeaderCell>
        {showAllColumns && (
          <React.Fragment>
            <TableHeaderCell width="25%" textAlign="right">
              {t("TIME_REPORTS.SUMMARY.PROJECTS.RATE")}
            </TableHeaderCell>
            <TableHeaderCell width="25%" textAlign="right">
              {t("TIME_REPORTS.SUMMARY.PROJECTS.AMOUNT")}
            </TableHeaderCell>
          </React.Fragment>
        )}
      </TableHeader>

      <TableBody>
        {groupedItems.map(group =>
          group.items.map((item, itemIndex) => (
            <TableRow key={item.payCode.guid}>
              {itemIndex === 0 && (
                <TableCell headerLabel={t("TIME_REPORTS.SUMMARY.PROJECTS.PAYCODE")} rowSpan={group.items.length}>
                  {item.payCode.name}
                </TableCell>
              )}
              <TableCell
                headerLabel={
                  variant === "hourly"
                    ? t("TIME_REPORTS.SUMMARY.PROJECTS.REPORTED_TIME_HOURLY")
                    : t("TIME_REPORTS.SUMMARY.PROJECTS.REPORTED_TIME_DAILY")
                }>
                {formatLoggedTime(item.reportedTime, item.payCode.measureUnit.code)}
              </TableCell>

              {showAllColumns && (
                <React.Fragment>
                  <TableCell
                    justifyContent={["flex-start", null, "flex-end"]}
                    headerLabel={t("TIME_REPORTS.SUMMARY.PROJECTS.RATE")}>
                    {formatRate(
                      item.effectiveClientRate,
                      timeReport.currency.code,
                      item.payCode.measureUnit.code === "DAILY" ? "d" : "h",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </TableCell>
                  <TableCell
                    justifyContent={["flex-start", null, "flex-end"]}
                    headerLabel={t("TIME_REPORTS.SUMMARY.PROJECTS.AMOUNT")}>
                    <Paragraph bold>
                      {formatCost(item.cost, timeReport.currency.code, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Paragraph>
                  </TableCell>
                </React.Fragment>
              )}
            </TableRow>
          )),
        )}

        <TableRow>
          <TableCell>
            <Paragraph bold>{t("TIME_REPORTS.SUMMARY.PROJECTS.TOTAL")}</Paragraph>
          </TableCell>
          <TableCell
            headerLabel={
              variant === "hourly"
                ? t("TIME_REPORTS.SUMMARY.PROJECTS.REPORTED_TIME_HOURLY")
                : t("TIME_REPORTS.SUMMARY.PROJECTS.REPORTED_TIME_DAILY")
            }>
            <Paragraph bold>{formatLoggedTime(totalTime, items[0]?.payCode.measureUnit.code ?? "HOURLY")}</Paragraph>
          </TableCell>

          {showAllColumns && (
            <React.Fragment>
              <TableCell />
              <TableCell
                justifyContent={["flex-start", null, "flex-end"]}
                headerLabel={t("TIME_REPORTS.SUMMARY.PROJECTS.AMOUNT")}>
                <Paragraph bold>
                  {formatCost(totalCost, timeReport.currency.code, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Paragraph>
              </TableCell>
            </React.Fragment>
          )}
        </TableRow>
      </TableBody>
    </Table>
  );
};
